import type { ReservationDto, ReservationResourceDto } from '../../api/reservation';

/**
 * 회의실 «예약 불가» 판정 — 일정 창에서 방을 고를 때 미리 보여 주는 용도 (2026-10-16).
 *
 * ⚠ 최종 판정은 여전히 서버다(자원 행을 잠근 채 겹침·수량을 본다 — reservation.service.ts).
 *   여기 판정은 «지금 보이는 예약»으로 한 추정이라, 이 사이 누가 잡으면 저장 때 409 가 날 수 있다.
 *
 * 규칙: 이 일정 시간 [start, end) 안의 «어느 순간이든» 겹친 예약 수량의 합이 방의 동시 수(capacity)에
 *   닿으면 불가. 겹친 예약을 «전부 더하지» 않는다 — 14:00~14:30 · 14:30~15:00 두 건은 동시 2건이 아니다.
 * 서버가 주는 예약은 이미 «자리를 차지하는 상태»(대기·확정)만이다.
 */

/** 이 시간 안에서 동시에 겹친 수량의 최댓값. */
export function maxConcurrent(reservations: Pick<ReservationDto, 'startAt' | 'endAt' | 'quantity'>[], start: Date, end: Date): number {
  const s0 = start.getTime();
  const e0 = end.getTime();
  const marks: [number, number][] = [];
  for (const r of reservations) {
    const s = Math.max(new Date(r.startAt).getTime(), s0);
    const e = Math.min(new Date(r.endAt).getTime(), e0);
    if (!(s < e)) continue;
    const q = r.quantity > 0 ? r.quantity : 1;
    marks.push([s, q], [e, -q]);
  }
  // 같은 시각이면 끝(-)을 먼저 — 맞닿은 두 예약은 겹친 것이 아니다.
  marks.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let cur = 0;
  let max = 0;
  for (const [, d] of marks) {
    cur += d;
    if (cur > max) max = cur;
  }
  return max;
}

/**
 * 이 시간에 예약할 수 없는 방의 id.
 * @param ignoreReservationId 이 일정이 이미 잡아 둔 예약 — 제 예약과 겹친다고 막으면 안 된다(수정 화면).
 */
export function unavailableRoomIds(
  resources: Pick<ReservationResourceDto, 'id' | 'capacity'>[],
  reservations: Pick<ReservationDto, 'id' | 'resourceId' | 'startAt' | 'endAt' | 'quantity'>[],
  start: Date,
  end: Date,
  ignoreReservationId?: string | null,
): Set<string> {
  const out = new Set<string>();
  for (const room of resources) {
    const mine = reservations.filter((r) => r.resourceId === room.id && r.id !== ignoreReservationId);
    if (mine.length === 0) continue;
    if (maxConcurrent(mine, start, end) >= Math.max(1, room.capacity)) out.add(room.id);
  }
  return out;
}

/** 서버 /reservation/status 가 한 번에 받는 기간 상한(48시간). 이보다 긴 일정은 미리 보지 않는다. */
export const STATUS_RANGE_MAX_MS = 2 * 24 * 60 * 60 * 1000;
